import { useState } from "react";
import { motion } from "framer-motion";
import { Shuffle } from "lucide-react";

const S = ({ children }) => (
  <svg viewBox="0 0 100 120" className="w-14 h-16 mx-auto fill-none stroke-current" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {children}
  </svg>
);

const CARDS = [
  {
    id: "fool", numeral: "0", name: "The Fool",
    symbol: <S><circle cx="70" cy="22" r="7" /><path d="M14 96 h26 l8 -16 h22" /><path d="M30 96 l7 14" /><circle cx="52" cy="52" r="4" /><path d="M52 56 v14 m-7 -6 h14" /></S>,
  },
  {
    id: "magician", numeral: "I", name: "The Magician",
    symbol: <S><line x1="50" y1="14" x2="50" y2="34" /><circle cx="50" cy="12" r="3" /><path d="M28 66 c0 -14 18 -14 22 0 c4 14 18 14 22 0 c4 -14 -18 -14 -22 0 c-4 14 -18 14 -22 0" /><line x1="50" y1="80" x2="50" y2="104" /><path d="M42 96 l16 0" /></S>,
  },
  {
    id: "priestess", numeral: "II", name: "The High Priestess",
    symbol: <S><line x1="26" y1="26" x2="26" y2="104" /><line x1="74" y1="26" x2="74" y2="104" /><path d="M26 26 h-6 m54 0 h-6" /><path d="M56 38 a22 22 0 1 0 0 52 a16 16 0 1 1 0 -52" /></S>,
  },
  {
    id: "empress", numeral: "III", name: "The Empress",
    symbol: <S><path d="M28 84 L28 52 L41 66 L50 42 L59 66 L72 52 L72 84 Z" /><circle cx="50" cy="30" r="4" /><path d="M40 96 h20" /></S>,
  },
  {
    id: "star", numeral: "XVII", name: "The Star",
    symbol: <S><line x1="50" y1="30" x2="50" y2="82" /><line x1="24" y1="56" x2="76" y2="56" /><line x1="32" y1="38" x2="68" y2="74" /><line x1="68" y1="38" x2="32" y2="74" /><circle cx="50" cy="56" r="5" /><path d="M20 100 q7 -7 15 0 q7 7 15 0 q7 -7 15 0 q7 7 15 0" /></S>,
  },
  {
    id: "moon", numeral: "XVIII", name: "The Moon",
    symbol: <S><path d="M58 26 a24 24 0 1 0 0 56 a18 18 0 1 1 0 -56" /><rect x="14" y="66" width="10" height="34" /><rect x="76" y="66" width="10" height="34" /><circle cx="30" cy="36" r="2" /><circle cx="24" cy="48" r="2" /></S>,
  },
  {
    id: "sun", numeral: "XIX", name: "The Sun",
    symbol: <S><circle cx="50" cy="60" r="16" /><circle cx="50" cy="60" r="7" /><line x1="50" y1="30" x2="50" y2="38" /><line x1="50" y1="82" x2="50" y2="90" /><line x1="20" y1="60" x2="28" y2="60" /><line x1="72" y1="60" x2="80" y2="60" /><line x1="29" y1="39" x2="35" y2="45" /><line x1="65" y1="75" x2="71" y2="81" /><line x1="71" y1="39" x2="65" y2="45" /><line x1="35" y1="75" x2="29" y2="81" /></S>,
  },
];

const CardFace = ({ card }) => (
  <div className="w-24 h-40 rounded-lg border border-gold/40 bg-gradient-to-b from-[#1c1826] to-[#12101a] shadow-[0_10px_30px_rgba(0,0,0,0.6)] flex flex-col items-center justify-between py-2.5 px-1 text-gold transition-shadow duration-300 hover:shadow-[0_0_35px_rgba(212,175,55,0.35)]">
    <span className="font-display text-[10px] tracking-widest text-gold/80">{card.numeral}</span>
    {card.symbol}
    <span className="font-serif text-[8.5px] uppercase tracking-[0.14em] text-parchment/90 text-center leading-tight px-1">
      {card.name}
    </span>
  </div>
);

export const TarotDeck = () => {
  const [order, setOrder] = useState(CARDS);
  const [deckKey, setDeckKey] = useState(0);

  const shuffle = () => {
    setOrder([...CARDS].sort(() => Math.random() - 0.5));
    setDeckKey((k) => k + 1);
  };

  const n = order.length;
  const mid = (n - 1) / 2;

  return (
    <div data-testid="tarot-deck" className="mt-20 text-center">
      <p className="text-xs uppercase tracking-[0.35em] text-gold/80 font-mono mb-3">
        The Major Arcana
      </p>
      <p className="text-sm text-parchment-muted mb-10 max-w-md mx-auto">
        Seven of the twenty-two elders of the deck. Hover to draw one from the fan —
        shuffle to begin the spread anew.
      </p>

      <div key={deckKey} className="relative h-[280px] sm:h-[300px] max-w-xl mx-auto select-none">
        {order.map((card, i) => {
          const off = i - mid;
          return (
            <motion.div
              key={card.id}
              data-testid={`tarot-card-${card.id}`}
              className="absolute bottom-0 left-1/2 cursor-pointer"
              style={{ marginLeft: -48, transformOrigin: "50% 130%", zIndex: i }}
              initial={{ rotate: 0, x: 0, y: 60, opacity: 0 }}
              animate={{ rotate: off * 9, x: off * 46, y: Math.abs(off) * 7, opacity: 1 }}
              transition={{ delay: 0.12 + i * 0.09, type: "spring", stiffness: 130, damping: 15 }}
              whileHover={{ y: -38, rotate: 0, scale: 1.12, zIndex: 40, transition: { duration: 0.25, delay: 0 } }}
            >
              <CardFace card={card} />
            </motion.div>
          );
        })}
      </div>

      <button
        data-testid="shuffle-deck-btn"
        onClick={shuffle}
        className="mt-6 inline-flex items-center gap-2 border border-gold/50 text-gold text-xs uppercase tracking-[0.25em] rounded-full px-7 py-3.5 hover:bg-gold hover:text-ink transition-colors duration-300"
      >
        <Shuffle size={15} /> Shuffle the Deck
      </button>
    </div>
  );
};
